import { Button, Text } from "@marketplace/ui";
import { useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MdOutlineRemoveRedEye } from "react-icons/md";

import { useGetProfileQuery } from "@/modules/profile";

import { useGetIndustryCategoryListQuery } from "../../hooks/queries/use-get-industry-category-list-query";
import type { AddProductFormSchema } from "../../hooks/schemas/use-add-product-form-schema";
import {
  PREVIEW_STORAGE_KEY,
  type ProductPreviewData
} from "../../pages/product-preview-page/types";
import type { ProductCategoryType } from "../../types";

type PromiseWithResolvers<T> = {
  promise: Promise<T>;
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason?: unknown) => void;
};

const fileToDataUrl = (file: File): Promise<string> => {
  const { promise, resolve, reject } = (
    Promise as PromiseConstructor & {
      withResolvers<T>(): PromiseWithResolvers<T>;
    }
  ).withResolvers<string>();
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result as string);
  reader.onerror = reject;
  reader.readAsDataURL(file);
  return promise;
};

type PreviewButtonProps = {
  productCategory?: ProductCategoryType;
};

export const PreviewButton = ({ productCategory }: PreviewButtonProps) => {
  const { t } = useTranslation("product", {
    keyPrefix: "addProductPage"
  });

  const formContext = useFormContext<AddProductFormSchema>();
  const { data: industryCategoryData } = useGetIndustryCategoryListQuery();
  const { data: profileData } = useGetProfileQuery();

  const handlePreviewClick = useCallback(async () => {
    if (!formContext) return;

    const formData = formContext.getValues();

    const industryCategories =
      industryCategoryData?.data?.filter((cat) =>
        formData.industryCategory?.includes(cat.slug_name)
      ) ?? [];

    const processedPictures = await Promise.all(
      (formData.pictures || []).map(async (pic: any) => {
        let mediaPath = pic.mediaPath || "";
        if (pic.file) {
          try {
            mediaPath = await fileToDataUrl(pic.file);
          } catch {
            // fallback
          }
        }
        return { mediaPath, type: pic.type || "image" };
      })
    );

    const previewData: ProductPreviewData = {
      productName: formData.productName || "",
      productDescription: formData.productDescription || "",
      productCategory: productCategory || "software",
      pictures: processedPictures,
      packages:
        formData.packages?.map(
          (pkg: AddProductFormSchema["packages"][number]) => ({
            packageId: pkg.packageId,
            name: pkg.name || "",
            isFreeTrial: pkg.isFreeTrial,
            specifications: pkg.specifications || [],
            variants:
              pkg.variants?.map(
                (
                  v: AddProductFormSchema["packages"][number]["variants"][number]
                ) => ({
                  variantId: v.variantId,
                  quantity: v.quantity || 0,
                  unit: v.unit || "",
                  price: pkg.isFreeTrial ? 0 : v.price,
                  provisioningId: v.provisioningId
                })
              ) || []
          })
        ) || [],
      provideTrial: formData.provideTrial || false,
      industryCategory: industryCategories.map((cat) => ({
        slug_name: cat.slug_name,
        name: cat.name
      })),
      companyBrandName: profileData?.data?.brand_name || formData.company || "",
      apiPackage: formData.apiPackage
        ? {
            host: formData.apiPackage.host,
            header: formData.apiPackage.header,
            key: formData.apiPackage.key,
            apis: formData.apiPackage.apis?.map(
              (api: AddProductFormSchema["apiPackage"]["apis"][number]) => ({
                name: api.name,
                endpoint: api.endpoint,
                method: api.method,
                body: api.body,
                response: api.response
              })
            )
          }
        : undefined
    };

    localStorage.setItem(PREVIEW_STORAGE_KEY, JSON.stringify(previewData));

    window.open("/products/list/preview", "_blank");
  }, [formContext, industryCategoryData, productCategory, profileData]);

  return (
    <div className="bg-zircon-haze-100 border border-solid border-mischka-400 rounded-lg py-[18px] px-6 w-[226px]">
      <div className="flex items-center justify-between gap-2">
        <Text type="heading" size="xxs" className="text-fjord-grey-400">
          {t("preview")}
        </Text>
        <Button
          variant="secondary"
          size="sm"
          rightSection={<MdOutlineRemoveRedEye size={14} />}
          onClick={handlePreviewClick}
        >
          {t("detailPage")}
        </Button>
      </div>
    </div>
  );
};
